import { IconInfoCircle } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  PUSH_BODY_MAX,
  PUSH_BODY_VISIBLE,
  PUSH_TITLE_MAX,
  PUSH_TITLE_VISIBLE,
  PushVariant,
  pushVariantError,
} from './push-variant';

/** One counter, with the two thresholds spelled out rather than colour-coded only. */
function Counter({
  length,
  visible,
  max,
  unit,
}: {
  length: number;
  visible: number;
  max: number;
  unit: string;
}) {
  if (length > max) {
    return (
      <p className="text-xs text-destructive">
        {length}/{max} characters — too long to send.
      </p>
    );
  }
  if (length > visible) {
    return (
      <p className="text-xs text-amber-600 dark:text-amber-500">
        {length}/{max} characters — most phones show about the first {visible}{' '}
        of the {unit} in a collapsed notification.
      </p>
    );
  }
  return (
    <p className="text-xs text-muted-foreground">
      {length}/{max} characters
    </p>
  );
}

/**
 * Push content for a multi-channel campaign.
 *
 * The one thing this editor must teach: a push is sent PER BATCH, not per
 * recipient. One OneSignal publish carries one title and one body for up to
 * 2000 people, so {{first_name}} cannot work here and is refused rather than
 * quietly rendered to a placeholder. {{brand}} is fine — it is the same for
 * everyone in the batch.
 */
export function PushVariantEditor({
  value,
  onChange,
  disabled,
}: {
  value: PushVariant;
  onChange: (variant: PushVariant) => void;
  disabled?: boolean;
}) {
  const set = <K extends keyof PushVariant>(
    key: K,
    fieldValue: PushVariant[K],
  ) => onChange({ ...value, [key]: fieldValue });

  const error = pushVariantError(value);
  const personalisationRejected =
    (error?.includes('{{first_name}}') || error?.includes('{{surname}}')) ??
    false;

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Push Notification</h4>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <IconInfoCircle size={14} />
          {'{{brand}} is supported; {{first_name}} and {{surname}} are not'}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="push-title">Title</Label>
        <Input
          id="push-title"
          value={value.title}
          onChange={(event) => set('title', event.target.value)}
          placeholder="Double data this weekend"
          disabled={disabled}
          className={cn(value.title.length > PUSH_TITLE_MAX && 'border-destructive')}
        />
        <Counter
          length={value.title.length}
          visible={PUSH_TITLE_VISIBLE}
          max={PUSH_TITLE_MAX}
          unit="title"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="push-body">Body</Label>
        <Textarea
          id="push-body"
          rows={3}
          value={value.body}
          onChange={(event) => set('body', event.target.value)}
          placeholder="Top up before Sunday and get double data on every bundle."
          disabled={disabled}
          className={cn(value.body.length > PUSH_BODY_MAX && 'border-destructive')}
        />
        <Counter
          length={value.body.length}
          visible={PUSH_BODY_VISIBLE}
          max={PUSH_BODY_MAX}
          unit="body"
        />
      </div>

      {personalisationRejected && (
        <p className="text-xs text-destructive">{error}</p>
      )}

      <p className="text-xs text-muted-foreground flex items-start gap-1">
        <IconInfoCircle size={13} className="mt-0.5 shrink-0" />
        Only customers with the app installed and notifications enabled can
        receive this. The preview count is consent only — Nova checks each
        device at send time, and customers with no active subscription finish as
        SKIPPED_NO_SUBSCRIPTION rather than as failures.
      </p>
    </div>
  );
}
